// src/hooks/useBlockedUsers.js
import { useCallback, useEffect, useState } from "react";

import {
  getBlockedUserIdsData,
  filterBlockedOwners,
  filterBlockedThreads,
} from "../services/moderationDataService";

/**
 * Zentraler Hook für blockierte Nutzer.
 *
 * Nutzung:
 * const { blockedUserIds, filterBlocked, addBlockedUser } = useBlockedUsers(myUid, isVerified);
 *
 * const visibleItems = filterBlocked(items);
 */
export const useBlockedUsers = (myUid, isVerified = true) => {
  const [blockedUserIds, setBlockedUserIds] = useState([]);
  const [blockedUsersLoading, setBlockedUsersLoading] = useState(false);

  useEffect(() => {
    let mounted = true;

    const loadBlockedUsers = async () => {
      if (!myUid || !isVerified) {
        setBlockedUserIds([]);
        return;
      }

      setBlockedUsersLoading(true);

      try {
        const ids = await getBlockedUserIdsData(myUid);

        if (!mounted) return;

        setBlockedUserIds(Array.isArray(ids) ? ids.map(String) : []);
      } catch (err) {
        console.warn("Blockierte Nutzer konnten nicht geladen werden:", err);

        if (!mounted) return;

        setBlockedUserIds([]);
      } finally {
        if (mounted) {
          setBlockedUsersLoading(false);
        }
      }
    };

    loadBlockedUsers();

    return () => {
      mounted = false;
    };
  }, [myUid, isVerified]);

  const addBlockedUser = useCallback((blockedUserId) => {
    if (!blockedUserId) return;

    setBlockedUserIds((prev) => {
      const id = String(blockedUserId);

      if (prev.map(String).includes(id)) {
        return prev;
      }

      return [...prev, id];
    });
  }, []);

  const removeBlockedUser = useCallback((blockedUserId) => {
    if (!blockedUserId) return;

    setBlockedUserIds((prev) =>
      prev.filter((id) => String(id) !== String(blockedUserId))
    );
  }, []);

  const filterBlocked = useCallback(
    (items = []) => {
      return filterBlockedOwners(items, blockedUserIds);
    },
    [blockedUserIds]
  );
  const filterBlockedThreadList = useCallback(
  (threads = []) => {
    return filterBlockedThreads(threads, blockedUserIds);
  },
  [blockedUserIds]
);

  const isBlocked = useCallback(
    (userId) => {
      if (!userId) return false;
      return blockedUserIds.map(String).includes(String(userId));
    },
    [blockedUserIds]
  );

 return {
  blockedUserIds,
  blockedUsersLoading,
  addBlockedUser,
  removeBlockedUser,
  filterBlocked,
  filterBlockedThreadList,
  isBlocked,
};
};